import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { TranslocoModule } from '@jsverse/transloco';

interface GwLatLng {
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * V8 advisory check for the integrator's site config tab. Computes:
 *
 * - Max non-ambiguous **frequency coverage** from the freq file content
 *   (formula `c / (2 × freq_step)` — same as inside `files-section.component.ts`).
 * - **Max gateway distance** (haversine) across the supplied gateway positions.
 * - **OK / Freq insufficient** verdict (informational; never blocks save).
 *
 * Lets the ORG see if the preset they picked has enough coverage for the layout BEFORE they
 * hit Save. If it doesn't, they can pick a wider preset from the catalog.
 */
@Component({
  selector: 'lib-site-coverage-check',
  standalone: true,
  imports: [CommonModule, TagModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (status() !== 'no-data') {
      <div
        class="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm"
        [class.border-aquamarine-300]="status() === 'ok'"
        [class.bg-aquamarine-50]="status() === 'ok'"
        [class.border-red-300]="status() === 'warning'"
        [class.bg-red-50]="status() === 'warning'">
        <span class="font-semibold uppercase tracking-wider text-gray-700">{{
          'siteCoverageCheck.title' | transloco
        }}</span>
        <span class="text-gray-700">
          {{ 'siteCoverageCheck.freqCoverage' | transloco }}: <strong>{{ formatMeters(freqCoverage()) }}</strong>
        </span>
        <span class="text-gray-700">
          {{ 'siteCoverageCheck.maxGwDistance' | transloco }}: <strong>{{ formatMeters(maxGatewayDistance()) }}</strong>
        </span>
        @if (status() === 'ok') {
          <p-tag
            [value]="'siteCoverageCheck.statusOk' | transloco"
            severity="success"
            icon="pi pi-check-circle"></p-tag>
        } @else {
          <p-tag
            [value]="'siteCoverageCheck.statusInsufficient' | transloco"
            severity="danger"
            icon="pi pi-exclamation-triangle"></p-tag>
          <span class="ml-1 text-xs text-red-700">
            {{ 'siteCoverageCheck.insufficientHint' | transloco }}
          </span>
        }
      </div>
    } @else if (freqCoverage() !== null && freqCoverage() !== undefined && gateways().length > 0) {
      <div class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        {{ 'siteCoverageCheck.needTwoGateways' | transloco }}
      </div>
    }
  `,
})
export class SiteCoverageCheckComponent {
  /** Pre-computed {@code c / (2 × freq_step)} metric in metres. Computed
   * upstream (today by the integrator's slim-preset projection, tomorrow
   * by the backend `IntegratorSitePreset.freqCoverage` field) so the
   * component never holds the raw freq table — Wheere calibration IP. */
  freqCoverage = input<number | null | undefined>(null);

  /** Reception-point positions (lat/lng) — the gateways' **antennas**, not the gateway
   *  chassis: the "Max GW dist" span must be measured across antennas. Only those with
   *  both coordinates set count toward maxDistance. */
  gateways = input<GwLatLng[]>([]);

  protected readonly maxGatewayDistance = computed<number | null>(() => {
    const positions = this.gateways().filter(p => p.latitude != null && p.longitude != null);
    if (positions.length < 2) return null;
    let max = 0;
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const d = haversine(
          positions[i].latitude as number,
          positions[i].longitude as number,
          positions[j].latitude as number,
          positions[j].longitude as number
        );
        if (d > max) max = d;
      }
    }
    return Math.round(max * 10) / 10;
  });

  protected readonly status = computed<'ok' | 'warning' | 'no-data'>(() => {
    const coverage = this.freqCoverage() ?? null;
    const distance = this.maxGatewayDistance();
    if (coverage == null || distance == null) return 'no-data';
    return distance > coverage ? 'warning' : 'ok';
  });

  protected formatMeters(v: number | null | undefined): string {
    if (v == null) return '—';
    return `${v}m`;
  }
}

// Coverage maths moved to {@code data/integrator-views.ts} as
// {@code calculateFreqCoverage} so it can run at projection time before the
// raw freq content is dropped from the slim preset.

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
